import Router from 'next/router';
import { memo, useMemo } from 'react';

import { ChevronDownIcon, LogOutIcon, SettingsIcon, UserIcon, XIcon } from '@air/design-icons';
import { type ActionSheetOptionItem } from '@air/primitive-action-sheet';
import { Avatar } from '@air/primitive-avatar';
import { IconButton } from '@air/primitive-icon-button';
import { useAirModal } from '@air/provider-modal';

import { WorkspacesActionSheet } from '~/components/CurrentWorkspaceNav/WorkspacesActionSheet';
import { ActionSheetModal } from '~/components/Mobile/ActionSheetModal';
import { Routes } from '~/constants/routes';
import { useCurrentWorkspacePermissionsContext } from '~/providers/CurrentWorkspacePermissionsProvider';
import { useCurrentWorkspace } from '~/providers/CurrentWorkspaceProvider';
import { useLogout } from '~/swr-hooks/auth/useLogout';
import { canSeeWorkspaceMembers } from '~/utils/permissions/workspacePermissions';

const AVATAR_SIZE = 36;

const RETINA_DISPLAY_FACTOR = 3;

export type CoreMobileWorkspaceNavigationHeaderProps = {
  onClose?: () => void;
};

export const CoreMobileWorkspaceNavigationHeader = memo(({ onClose }: CoreMobileWorkspaceNavigationHeaderProps) => {
  const { currentWorkspace } = useCurrentWorkspace();
  const [showWorkspacesActionSheet] = useAirModal(WorkspacesActionSheet);
  const [showActionSheet, hideActionSheet] = useAirModal(ActionSheetModal);
  const { data: permissions } = useCurrentWorkspacePermissionsContext();
  const { logout } = useLogout();
  const canViewMembers = canSeeWorkspaceMembers(permissions);

  const options = useMemo(() => {
    const optionsArray: ActionSheetOptionItem[] = [];

    if (canViewMembers) {
      optionsArray.push({
        id: 'workspace-settings',
        label: 'Workspace settings',
        onSelect: () => {
          Router.push(Routes.workspaceSettings.members);
          hideActionSheet();
        },
        prefix: <SettingsIcon size="sm" />,
        type: 'item',
      });
    }

    optionsArray.push({
      id: 'account-settings',
      label: 'Account settings',
      onSelect: () => {
        Router.push(Routes.account.myAccount);
        hideActionSheet();
      },
      prefix: <UserIcon size="sm" />,
      type: 'item',
    });

    optionsArray.push({
      id: 'logout',
      label: 'Log out',
      prefix: <LogOutIcon size="sm" />,
      onSelect: () => {
        logout();
        hideActionSheet();
      },
      type: 'item',
    });

    return optionsArray;
  }, [canViewMembers, hideActionSheet, logout]);

  if (!currentWorkspace) {
    return null;
  }

  return (
    <header className="flex h-16 items-center justify-between border-b ads:border-b-ads-gray-5 border-b-gray-5 py-4 pr-2 pl-1">
      <button
        className="flex cursor-pointer items-center gap-4 rounded-4 p-1 transition-colors ads:hover:bg-hover-subtle hover:bg-gray-3"
        // biome-ignore lint/performance/noJsxPropsBind: PLA-1724 — pre-existing debt, not introduced by the icon migration
        onClick={() => showWorkspacesActionSheet()}
        type="button"
      >
        <Avatar
          appearance="square"
          size={AVATAR_SIZE}
          src={`${currentWorkspace.image}?h=${AVATAR_SIZE * RETINA_DISPLAY_FACTOR}&w=${
            AVATAR_SIZE * RETINA_DISPLAY_FACTOR
          }=&fit=crop`}
        />

        <div className="flex items-center gap-1">
          <h2 className="font-semibold ads:text-secondary text-16 text-gray-11">{currentWorkspace.name}</h2>
          <ChevronDownIcon className="ads:text-icon-default text-gray-10" size="sm" />
        </div>
      </button>
      <div className="flex items-center gap-4">
        <IconButton
          adsIcon
          appearance="ghost"
          color="gray"
          icon={SettingsIcon}
          label="Settings"
          // biome-ignore lint/performance/noJsxPropsBind: PLA-1724 — pre-existing debt, not introduced by the icon migration
          onClick={() =>
            showActionSheet({
              options,
              title: 'Settings',
            })
          }
          size="large"
        />
        {!!onClose && (
          <>
            <div className="h-8 w-px bg-gray-5" />
            <IconButton
              adsIcon
              appearance="ghost"
              color="gray"
              icon={XIcon}
              label="Close"
              onClick={onClose}
              size="large"
            />
          </>
        )}
      </div>
    </header>
  );
});

CoreMobileWorkspaceNavigationHeader.displayName = 'CoreMobileWorkspaceNavigationHeader';
